import React, { Component } from "react";
import { get as _get } from "lodash";
import { Link } from "react-router-dom";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";

import { showAlertMessage, HighChart } from "../../resusableComponents/common/formElements";
import { withRouter } from "../../resusableComponents/hoc/withRouter";

import { getCompetitiveAnalysisReport } from "../../redux/services/competitiveAnalysis";
import { updateLoadingState } from "../../redux/actions/application";

// ─── Brand colors ─────────────────────────────────────────────────────────────
const BRAND = {
  hotPink: "#FA6284",
  skyAqua: "#59C8EA",
  mustard: "#FFDE59",
  black: "#000000",
  white: "#FFFFFF",
  greyLight: "#E6E6E6",
  greyDark: "#333333",
};

// Category color maps (keyed by category name)
const VP_COLORS = {
  "Security & Governance": { stroke: BRAND.skyAqua, textWhite: false },
  "Scalability & Performance": { stroke: BRAND.hotPink, textWhite: true },
  "Cloud Cost Optimization": { stroke: BRAND.mustard, textWhite: false },
  "AI & Automation Enablement": { stroke: BRAND.black, textWhite: true },
};
const PP_COLORS = {
  "Security Vulnerabilities": { stroke: BRAND.hotPink, textWhite: true },
  "Technical Debt & Legacy Systems": { stroke: BRAND.black, textWhite: true },
  "Data Silos & Fragmentation": { stroke: BRAND.skyAqua, textWhite: false },
  "System Downtime": { stroke: BRAND.mustard, textWhite: false },
};

const FALLBACK_COLORS = [BRAND.skyAqua, BRAND.hotPink, BRAND.mustard, BRAND.black];

function buildCategories(totalShareOfVoice, colorMap) {
  return Object.entries(totalShareOfVoice || {}).map(([name, value], i) => ({
    id: `cat-${i}`,
    name,
    value: Number(value) || 0,
    stroke: _get(colorMap, [name, "stroke"], FALLBACK_COLORS[i % FALLBACK_COLORS.length]),
    textWhite: _get(colorMap, [name, "textWhite"], false),
  }));
}

// ─── Blank initial state helpers ──────────────────────────────────────────────
const BLANK_REPORT_STATE = {
  competitorList: [],
  reportData: [],       // array of per-competitor data objects
  marketGap: {
    targetingInferences: "",
    dominantMarketAngle: "",
    whitespaceOpportunity: "",
  },
};

// ─── TABS ─────────────────────────────────────────────────────────────────────


// ─── SVG Charts removed in favor of Highcharts ─────────────────────────────────

// ─── Main Component ───────────────────────────────────────────────────────────
class ViewCompetitiveAnalysisReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mainClient: "",
      analysisId: _get(this.props, "params.id", 0),
      activeView: "valuePropositions",
      selectedCompetitor: "All Competitors",
      isLoading: false,

      // Blank data (filled after API)
      ...BLANK_REPORT_STATE,
    };
  }

  componentDidMount() {
    this._fetchReport();
  }

  componentDidUpdate(prevProps) {
    const prevId = _get(prevProps, "params.id", 0);
    const currId = _get(this.props, "params.id", 0);
    if (prevId !== currId) {
      this._fetchReport();
    }
  }

  _fetchReport = async () => {
    const { analysisId } = this.state;
    if (!analysisId) { return; }

    this.setState({ isLoading: true, ...BLANK_REPORT_STATE });

    try {
      const response = await getCompetitiveAnalysisReport(analysisId);

      if (_get(response, "flag", false)) {
        const data = _get(response, "data", {});
        const mainClient = _get(data, "main_client", "");
        const competitorList = _get(data, "competitive_list", []);
        const rawReportData = _get(data, "report_data", []);

        // Build normalised per-competitor objects
        const reportData = rawReportData.map((item) => {
          const d = _get(item, "data", {});
          const vpSov = _get(d, "ad_intelligence.chartData.valuePropositions.totalShareOfVoice", {});
          const ppSov = _get(d, "ad_intelligence.chartData.painPoints.totalShareOfVoice", {});
          const ads = _get(d, "ad_intelligence.adIntelligence", []);

          return {
            competitiveId: _get(item, "competitive_id", ""),
            competitiveName: _get(item, "competitive_name", ""),
            startDate: _get(item, "start_date", ""),
            endDate: _get(item, "end_date", ""),
            status: _get(d, "status", false),
            errorMessage: _get(d, "error_message", null),

            // Audience targeting
            primaryTarget: _get(d, "competitor_analysis.primaryTarget", null),
            secondaryTarget: _get(d, "competitor_analysis.secondaryTarget", null),
            notTargeting: _get(d, "competitor_analysis.notTargeting", null),
            summary: _get(d, "competitor_analysis.summary", ""),

            // Market gap (first competitor wins for global summary)
            marketGap: {
              targetingInferences: _get(d, "market_gap_analysis.targetingInferences", ""),
              dominantMarketAngle: _get(d, "market_gap_analysis.dominantMarketAngle", ""),
              whitespaceOpportunity: _get(d, "market_gap_analysis.whitespaceOpportunity", ""),
            },

            // Charts
            vpCategories: buildCategories(vpSov, VP_COLORS),
            ppCategories: buildCategories(ppSov, PP_COLORS),
            vpMonthlyTrend: _get(d, "ad_intelligence.chartData.valuePropositions.monthlyTrend", []),
            ppMonthlyTrend: _get(d, "ad_intelligence.chartData.painPoints.monthlyTrend", []),

            // Ads (normalised)
            ads: (ads || []).map((ad, idx) => ({
              id: _get(ad, "adId", `ad-${idx}`),
              competitor: _get(ad, "advertiserName", _get(item, "competitive_name", "")),
              duration: _get(ad, "adDurationDays", 0),
              tone: _get(ad, "messagingTone", ""),
              adUrl: _get(ad, "adId", "#"),
              productsPromoted: _get(ad, "productsPromoted", []),
              // Map pain point / value prop to category types
              vpCategory: _get(ad, "valueProposition.category", null),
              vpQuote: _get(ad, "valueProposition.rawTextQuote", ""),
              ppCategory: _get(ad, "painPoint.category", null),
              ppQuote: _get(ad, "painPoint.rawTextQuote", ""),
            })),
          };
        });

        // Global market gap: use first competitor with data
        const firstWithGap = reportData.find((r) => r.marketGap.targetingInferences);
        const marketGap = firstWithGap ? firstWithGap.marketGap : BLANK_REPORT_STATE.marketGap;

        this.setState({
          mainClient,
          competitorList,
          reportData,
          marketGap,
          isLoading: false,
        });
      } else {
        showAlertMessage(_get(response, "message", this.props.intl.formatMessage({ id: "competitive_analysis.view_report.error.fetch_report", defaultMessage: "Something went wrong while fetching report." })));
        this.setState({ isLoading: false });
      }
    } catch (err) {
      showAlertMessage(_get(err, "message", this.props.intl.formatMessage({ id: "competitive_analysis.view_report.error.fetch_report", defaultMessage: "Something went wrong while fetching report." })));
      this.setState({ isLoading: false });
    }
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  _getCategoryBgClass = (hex) => {
    switch (hex) {
      case "#FA6284": return "ca-bg-primary";
      case "#59C8EA": return "ca-bg-secondary";
      case "#FFDE59": return "ca-bg-warning";
      case "#000000": return "ca-bg-black";
      default: return "ca-bg-black";
    }
  };

  _getActiveCompetitorData = () => {
    const { selectedCompetitor, reportData } = this.state;
    //if (selectedCompetitor === "All Competitors") { return reportData; }
    return reportData.filter((r) => r.competitiveName === selectedCompetitor);
  };

  _getCurrentCategories = () => {
    const { activeView } = this.state;
    const activeData = this._getActiveCompetitorData();
    if (!activeData.length) { return []; }

    // Merge totals across selected competitors
    const merged = {};
    activeData.forEach((r) => {
      const cats = activeView === "valuePropositions" ? r.vpCategories : r.ppCategories;
      (cats || []).forEach((cat) => {
        if (!merged[cat.name]) {
          merged[cat.name] = { ...cat };
        } else {
          merged[cat.name].value += cat.value;
        }
      });
    });
    return Object.values(merged);
  };

  _getCurrentMonthlyTrend = () => {
    const { activeView } = this.state;
    const activeData = this._getActiveCompetitorData();
    if (!activeData.length) { return []; }

    const merged = {};
    activeData.forEach((r) => {
      const trends = activeView === "valuePropositions" ? r.vpMonthlyTrend : r.ppMonthlyTrend;
      (trends || []).forEach((t) => {
        if (!merged[t.month]) {
          merged[t.month] = { month: t.month, categories: { ...t.categories } };
        } else {
          Object.keys(t.categories || {}).forEach((catName) => {
            merged[t.month].categories[catName] = (merged[t.month].categories[catName] || 0) + (t.categories[catName] || 0);
          });
        }
      });
    });
    return Object.values(merged);
  };

  _getFilteredAds = () => {
    const { activeView } = this.state;
    const activeData = this._getActiveCompetitorData();
    const allAds = activeData.flatMap((r) => r.ads || []);

    if (activeView === "valuePropositions") {
      return allAds.filter((ad) => ad.vpCategory);
    }
    return allAds.filter((ad) => ad.ppCategory);
  };

  // ─── Renders ─────────────────────────────────────────────────────────────────

  _renderHeader = () => {
    const { selectedCompetitor, competitorList } = this.state;
    const activeData = this._getActiveCompetitorData();
    const startDate = activeData.length > 0 ? activeData[0].startDate : "";
    const endDate = activeData.length > 0 ? activeData[0].endDate : "";

    return (
      <div className="ca-header d-flex flex-wrap justify-content-between align-items-center gap-3 mb-5">
        <div className="d-flex align-items-center gap-3">
          <div className="ca-brand-logo">RD</div>
          <h2 className="ca-page-title mb-0">
            <FormattedMessage id="competitive_analysis.view_report.messaging_intelligence" defaultMessage="Messaging Intelligence" />
          </h2>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-3">
          {startDate && endDate && (
            <div>
              <span className="btn btn-outline-secondary waves-effect px-3 py-2 ca-pointer-none">
                {`${startDate} - ${endDate}`}
              </span>
            </div>
          )}
          <div className="ca-filter-pill">
            <span className="ca-filter-icon">🔽</span>
            <select
              className="ca-filter-select"
              value={selectedCompetitor}
              onChange={(e) => this.setState({ selectedCompetitor: e.target.value })}
            >
              {(competitorList || []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    );
  };

  _renderAiSummary = () => {
    const activeData = this._getActiveCompetitorData();
    const marketGap = activeData.length > 0 && activeData[0].marketGap
      ? activeData[0].marketGap
      : BLANK_REPORT_STATE.marketGap;
    const { targetingInferences, dominantMarketAngle, whitespaceOpportunity } = marketGap;

    return (
      <div className="ca-ai-summary mb-5">
        <div className="ca-ai-summary__icon">⚡</div>
        <div className="ca-ai-summary__body">
          <h3 className="ca-ai-summary__heading">
            <FormattedMessage id="competitive_analysis.view_report.ai_strategic_summary" defaultMessage="AI Strategic Summary" />
          </h3>
          <div className="row g-4">
            <div className="col-lg-4">
              <h4 className="ca-ai-summary__subheading">
                <FormattedMessage id="competitive_analysis.view_report.targeting_inferences" defaultMessage="Targeting Inferences" />
              </h4>
              <p className="ca-ai-summary__text">
                {targetingInferences || <span className="text-muted"><FormattedMessage id="competitive_analysis.view_report.no_content_available" defaultMessage="No content available." /></span>}
              </p>
            </div>
            <div className="col-lg-4">
              <h4 className="ca-ai-summary__subheading">
                <FormattedMessage id="competitive_analysis.view_report.dominant_market_angle" defaultMessage="Dominant Market Angle" />
              </h4>
              <p className="ca-ai-summary__text">
                {dominantMarketAngle || <span className="text-muted"><FormattedMessage id="competitive_analysis.view_report.no_content_available" defaultMessage="No content available." /></span>}
              </p>
            </div>
            <div className="col-lg-4">
              <h4 className="ca-ai-summary__subheading">
                <FormattedMessage id="competitive_analysis.view_report.whitespace_opportunity" defaultMessage="Whitespace Opportunity" />
              </h4>
              <p className="ca-ai-summary__text">
                {whitespaceOpportunity || <span className="text-muted"><FormattedMessage id="competitive_analysis.view_report.no_content_available" defaultMessage="No content available." /></span>}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  _renderTabs = () => {
    const { activeView } = this.state;
    const { intl } = this.props;
    const TABS = [
      { id: "valuePropositions", label: intl.formatMessage({ id: "competitive_analysis.view_report.tabs.value_propositions", defaultMessage: "Value Propositions" }), icon: "🛡️" },
      { id: "painPoints", label: intl.formatMessage({ id: "competitive_analysis.view_report.tabs.pain_points", defaultMessage: "Pain Points" }), icon: "🔧" },
      { id: "audience", label: intl.formatMessage({ id: "competitive_analysis.view_report.tabs.audience_targeting", defaultMessage: "Audience Targeting" }), icon: "👥" },
    ];
    return (
      <div className="ca-tabs mb-5">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`ca-tab-btn${activeView === tab.id ? " ca-tab-btn--active" : ""}`}
            onClick={() => this.setState({ activeView: tab.id })}
          >
            <span className="ca-tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  _renderChartsRow = () => {
    const { intl } = this.props;
    const currentCategories = this._getCurrentCategories();
    const currentMonthlyTrend = this._getCurrentMonthlyTrend();
    const hasData = currentCategories.some((c) => c.value > 0);

    const totalDays = currentCategories.reduce((s, c) => s + c.value, 0);

    const lineChartOptions = {
      chart: { type: "line", height: 290 },
      title: { text: null },
      xAxis: { categories: currentMonthlyTrend.map((t) => t.month), crosshair: true },
      yAxis: { min: 0, title: { text: null } },
      plotOptions: {
        line: {
          marker: { enabled: true, radius: 4 }
        }
      },
      series: currentCategories.map((cat) => ({
        name: cat.name,
        data: currentMonthlyTrend.map((t) => t.categories[cat.name] || 0),
        color: cat.stroke
      })),
      credits: { enabled: false },
      legend: { enabled: true }
    };

    const donutChartOptions = {
      chart: { type: "pie", height: 290 },
      title: {
        text: `${intl.formatMessage({ id: "competitive_analysis.view_report.total_days", defaultMessage: "Total Days" })}<br/>${totalDays}`,
        align: 'center',
        verticalAlign: 'middle',
        y: -30,
        style: { fontSize: '14px', textAlign: 'center', fontWeight: 'bold' }
      },
      tooltip: { pointFormat: `<b>{point.y}</b> ${intl.formatMessage({ id: "competitive_analysis.view_report.days_lowercase", defaultMessage: "days" })}` },
      plotOptions: {
        pie: {
          innerSize: '70%',
          dataLabels: { enabled: false },
          showInLegend: true,
          borderWidth: 0
        }
      },
      series: [{
        name: intl.formatMessage({ id: "competitive_analysis.view_report.days_lowercase", defaultMessage: "days" }),
        colorByPoint: true,
        data: currentCategories.map((cat) => ({
          name: cat.name,
          y: cat.value,
          color: cat.stroke
        }))
      }],
      credits: { enabled: false }
    };

    return (
      <div className="row g-4 mb-5">
        <div className="col-lg-8">
          <div className="ca-chart-card h-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="ca-chart-title mb-0">
                <FormattedMessage id="competitive_analysis.view_report.active_ads_by_category" defaultMessage="Active Ads by Category" />
              </h4>
              <span className="ca-chart-icon">☰</span>
            </div>
            {(!currentMonthlyTrend || currentMonthlyTrend.length === 0) ? (
              <div className="ca-empty-column"><FormattedMessage id="competitive_analysis.view_report.no_content_available" defaultMessage="No content available." /></div>
            ) : (
              <HighChart options={lineChartOptions} />
            )}
          </div>
        </div>
        <div className="col-lg-4">
          <div className="ca-chart-card h-100">
            <h4 className="ca-chart-title mb-4">
              <FormattedMessage id="competitive_analysis.view_report.total_share_of_voice" defaultMessage="Total Share of Voice" />
            </h4>
            {!hasData ? (
              <div className="ca-empty-column"><FormattedMessage id="competitive_analysis.view_report.no_content_available" defaultMessage="No content available." /></div>
            ) : (
              <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-4">
                <div className="ca-donut-container">
                  <HighChart options={donutChartOptions} />
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  _renderAdKanban = () => {
    const { activeView } = this.state;
    const currentCategories = this._getCurrentCategories();
    const filteredAds = this._getFilteredAds();

    if (!currentCategories.length) {
      return <div className="ca-empty-column"><FormattedMessage id="competitive_analysis.view_report.no_content_available" defaultMessage="No content available." /></div>;
    }

    return (
      <div className="ca-kanban-scroll row">
        {currentCategories.map((category) => {
          const categoryAds = filteredAds.filter((ad) =>
            activeView === "valuePropositions"
              ? ad.vpCategory === category.name
              : ad.ppCategory === category.name
          );

          return (
            <div key={category.id} className="ca-kanban-column">
              <div className="ca-kanban-column__header">
                <div className="d-flex align-items-center gap-2">
                  <span className={`ca-kanban-dot ${this._getCategoryBgClass(category.stroke)}`} />
                  <h5 className="ca-kanban-column__title mb-0">{category.name}</h5>
                </div>
                <span className="ca-kanban-badge">{categoryAds.length}</span>
              </div>
              <div className="ca-kanban-cards">
                {categoryAds.map((ad) => (
                  <div key={ad.id} className="ca-ad-card">
                    <div className={`ca-ad-card__accent ${this._getCategoryBgClass(category.stroke)}`} />
                    <div className="ca-ad-card__top">
                      <div className="d-flex align-items-center gap-2">
                        <div className="ca-avatar">{(ad.competitor || "?").charAt(0)}</div>
                        <span className="ca-ad-card__competitor">{ad.competitor}</span>
                      </div>
                      <span className="ca-ad-card__duration">
                        <FormattedMessage id="competitive_analysis.view_report.days" defaultMessage="{days} Days" values={{ days: ad.duration }} />
                      </span>
                    </div>
                    {activeView === "valuePropositions" && ad.vpQuote && (
                      <p className="ca-ad-card__copy">"{ad.vpQuote}"</p>
                    )}
                    {activeView === "painPoints" && ad.ppQuote && (
                      <p className="ca-ad-card__copy">"{ad.ppQuote}"</p>
                    )}
                    {(ad.productsPromoted || []).length > 0 && (
                      <div className="ca-ad-card__products mb-2">
                        {ad.productsPromoted.map((p, i) => (
                          <span key={i} className="ca-tone-badge me-1">{p}</span>
                        ))}
                      </div>
                    )}
                    <div className="ca-ad-card__footer">
                      {ad.tone && (
                        <span className="ca-tone-badge">
                          <FormattedMessage id="competitive_analysis.view_report.tone" defaultMessage="Tone: {tone}" values={{ tone: ad.tone }} />
                        </span>
                      )}
                      {ad.adUrl && ad.adUrl !== "#" && (
                        <a href={ad.adUrl} target="_blank" rel="noopener noreferrer" className="ca-view-ad-btn">
                          <FormattedMessage id="competitive_analysis.view_report.view_ad" defaultMessage="View Ad ↗" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {categoryAds.length === 0 && (
                  <div className="ca-empty-column"><FormattedMessage id="competitive_analysis.view_report.no_content_available" defaultMessage="No content available." /></div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  _renderAudienceTargeting = () => {
    const { selectedCompetitor } = this.state;
    const activeData = this._getActiveCompetitorData();

    return (
      <div className="ca-audience">
        <div className="d-flex align-items-center gap-3 mb-4">
          <div className="ca-audience__icon-wrap">👥</div>
          <div>
            <h3 className="ca-page-title mb-1">
              <FormattedMessage id="competitive_analysis.view_report.audience_targeting" defaultMessage="Audience Targeting" />
            </h3>
            <p className="ca-muted-text mb-0">
              <FormattedMessage id="competitive_analysis.view_report.inferred_from_messaging" defaultMessage="{competitor} — Inferred from messaging patterns." values={{ competitor: selectedCompetitor }} />
            </p>
          </div>
        </div>

        {activeData.length === 0 ? (
          <div className="ca-empty-state">
            <div className="ca-empty-state__icon">👤</div>
            <h5 className="ca-empty-state__title">
              <FormattedMessage id="competitive_analysis.view_report.no_targeting_data" defaultMessage="No Targeting Data Available" />
            </h5>
            <p className="ca-empty-state__text">
              <FormattedMessage id="competitive_analysis.view_report.no_data_found_filter" defaultMessage="No data found for the selected filter." />
            </p>
          </div>
        ) : (
          activeData.map((compData) => (
            <div key={compData.competitiveId} className="mb-6">
              {activeData.length > 1 && (
                <h5 className="ca-chart-title mb-4">{compData.competitiveName}</h5>
              )}

              {(!compData.primaryTarget && !compData.secondaryTarget && !compData.notTargeting) ? (
                <div className="ca-empty-state">
                  <div className="ca-empty-state__icon">👤</div>
                  <h5 className="ca-empty-state__title">
                    <FormattedMessage id="competitive_analysis.view_report.no_targeting_data" defaultMessage="No Targeting Data Available" />
                  </h5>
                  <p className="ca-empty-state__text">
                    <FormattedMessage id="competitive_analysis.view_report.no_content_available_for" defaultMessage="No content available for {competitor}." values={{ competitor: compData.competitiveName }} />
                  </p>
                </div>
              ) : (
                <div className="row g-4">
                  {/* Primary Target */}
                  {compData.primaryTarget && (
                    <div className="col-lg-6">
                      <div className="ca-targeting-card ca-targeting-card--primary h-100">
                        <div className="d-flex align-items-center gap-3 mb-4">
                          <div className="ca-targeting-icon ca-targeting-icon--primary">🎯</div>
                          <h4 className="ca-chart-title mb-0">
                            <FormattedMessage id="competitive_analysis.view_report.primary_target" defaultMessage="Primary Target: {target}" values={{ target: _get(compData.primaryTarget, "title", "") }} />
                          </h4>
                        </div>
                        <h6 className="fw-bold mb-2 ca-role-heading">
                          <FormattedMessage id="competitive_analysis.view_report.likely_roles" defaultMessage="Likely roles:" />
                        </h6>
                        <ul className="ca-role-list mb-4">
                          {(_get(compData.primaryTarget, "roles", []) || []).map((role, i) => <li key={i}>{role}</li>)}
                        </ul>
                        {(_get(compData.primaryTarget, "why", []) || []).length > 0 && (
                          <div className="ca-why-box ca-why-box--primary mb-4">
                            <div className="ca-why-box__header">
                              <FormattedMessage id="competitive_analysis.view_report.why" defaultMessage="Why" />
                            </div>
                            <ul className="ca-role-list p-4 px-6 mb-0">
                              {(_get(compData.primaryTarget, "why", []) || []).map((reason, i) => <li key={i}>{reason}</li>)}
                            </ul>
                          </div>
                        )}
                        <p className="ca-body-text mb-0">{_get(compData.primaryTarget, "conclusion", "")}</p>
                      </div>
                    </div>
                  )}

                  {/* Secondary Target */}
                  {compData.secondaryTarget && (
                    <div className="col-lg-6">
                      <div className="ca-targeting-card ca-targeting-card--secondary h-100">
                        <div className="d-flex align-items-center gap-3 mb-4">
                          <div className="ca-targeting-icon ca-targeting-icon--secondary">💼</div>
                          <h4 className="ca-chart-title mb-0">
                            <FormattedMessage id="competitive_analysis.view_report.secondary_target" defaultMessage="Secondary Target: {target}" values={{ target: _get(compData.secondaryTarget, "title", "") }} />
                          </h4>
                        </div>
                        <h6 className="fw-bold mb-2 ca-role-heading">
                          <FormattedMessage id="competitive_analysis.view_report.likely_roles" defaultMessage="Likely roles:" />
                        </h6>
                        <ul className="ca-role-list mb-4">
                          {(_get(compData.secondaryTarget, "roles", []) || []).map((role, i) => <li key={i}>{role}</li>)}
                        </ul>
                        {(_get(compData.secondaryTarget, "why", []) || []).length > 0 && (
                          <div className="ca-why-box ca-why-box--secondary mb-4">
                            <div className="ca-why-box__header">
                              <FormattedMessage id="competitive_analysis.view_report.why" defaultMessage="Why" />
                            </div>
                            <ul className="ca-role-list p-4 px-6 mb-0">
                              {(_get(compData.secondaryTarget, "why", []) || []).map((reason, i) => <li key={i}>{reason}</li>)}
                            </ul>
                          </div>
                        )}
                        <p className="ca-body-text mb-0">{_get(compData.secondaryTarget, "conclusion", "")}</p>
                      </div>
                    </div>
                  )}

                  {/* Not Targeting */}
                  {compData.notTargeting && (
                    <div className="col-lg-6">
                      <div className="ca-targeting-card ca-targeting-card--not h-100">
                        <div className="d-flex align-items-center gap-3 mb-4">
                          <div className="ca-targeting-icon ca-targeting-icon--not">🚫</div>
                          <h4 className="ca-chart-title mb-0">
                            {_get(compData.notTargeting, "title", this.props.intl.formatMessage({ id: "competitive_analysis.view_report.not_targeting_strongly", defaultMessage: "Who They Are NOT Targeting Strongly" }))}
                          </h4>
                        </div>
                        <ul className="ca-role-list mb-4">
                          {(_get(compData.notTargeting, "roles", []) || []).map((role, i) => <li key={i}>{role}</li>)}
                        </ul>
                        <p className="ca-body-text mb-0">{_get(compData.notTargeting, "conclusion", "")}</p>
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  {compData.summary && (
                    <div className="col-lg-6">
                      <div className="ca-summary-card h-100">
                        <h4 className="ca-chart-title mb-4">
                          <FormattedMessage id="competitive_analysis.view_report.summary" defaultMessage="Summary" />
                        </h4>
                        <p className="ca-body-text">{compData.summary}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  // ─── Main render ─────────────────────────────────────────────────────────────

  render() {
    const { activeView, isLoading, mainClient } = this.state;

    if (isLoading) {
      return (
        <div className="ca-report-wrapper ca-report-wrapper--loading d-flex justify-content-center align-items-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden"><FormattedMessage id="competitive_analysis.view_report.loading" defaultMessage="Loading..." /></span>
          </div>
        </div>
      );
    }

    return (
      <div className="container-fluid flex-grow-1 px-6 py-6">
        <div className="row mb-4">
          <div className="col-lg-7 d-flex align-items-center gap-3">
            <h1 className="heading-04 fw-semibold text-dark-35 text-uppercase mb-0">
              <FormattedMessage id="competitive_analysis.view_report.title" defaultMessage="Competitive Analysis Report" values={{ clientName: (mainClient ? ` - ${mainClient}` : "") }} />
            </h1>
          </div>
          <div className="col-lg-5 d-flex justify-content-end gap-3">
            <Link
              id="btn-back-to-list"
              className="btn btn-outline-primary waves-effect"
              to="/competitive-analysis"
            >
              <FormattedMessage id="competitive_analysis.view_report.back_to_report_list" defaultMessage="Back to Report List" />
            </Link>
          </div>
        </div>
        <div className="ca-report-wrapper">
          {this._renderHeader()}
          {this._renderAiSummary()}
          {this._renderTabs()}

          {(activeView === "valuePropositions" || activeView === "painPoints") && (
            <>
              {this._renderChartsRow()}
              {this._renderAdKanban()}
            </>
          )}

          {activeView === "audience" && this._renderAudienceTargeting()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
});

const mapDispatchToProps = (dispatch) => ({
  updateLoadingState: (data) => dispatch(updateLoadingState(data))
});
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ViewCompetitiveAnalysisReport)));
